import { ObjectId } from '@navstation-portal/shared/data-model/common';
import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { StateField, StateFieldType } from '../report.state';

export type StockTanks = Record<string, StockTank | undefined>;

export interface StockOptionsState {
  tankSounding: boolean;
  // V1's stock.tank_based_reporting. The Bunker and Stock tabs draw the tank
  // tables while it is on, and each new report takes it from the report before.
  tankBasedReporting?: boolean;
}

export type TankTransfersState = TankTransfer[];

export interface TankTransfer {
  from: string;
  to: string;
  quantity: number;
}

export interface TankTransferFormState {
  [key: string]: StateField<StateFieldType>;
  from: StateField<SelectOption | undefined>;
  to: StateField<SelectOption | undefined>;
  quantity: StateField<number | undefined>;
}

export interface FuelWaterRobState {
  bilgeWater: number;
  dischargedSludge: number;
  dischargedBilgeWater: number;
  freshWater: number;
  distilledWater: number;
  tankCleaningChemical: number;
}

export interface LubeOilRobState {
  lubeOilSystem: number;
  lubeOilSpare: number;
  lowBnCylinderOil: number;
  highBnCylinderOil: number;
  aeLubeOil: number;
  turboOil: number;
  hydraulicOil: number;
  otherOil: number;
}

export interface OilLossesState {
  totalWasteOil: number;
  incineratedWasteOil: number;
  evaporatedWaterFromWasteOil: number;
  sludgeRob: number;
  dischargedWasteOil: number;
}

export interface StockTank {
  _id: ObjectId;
  amount: number;
  amountBeforeBunkering: number;
  bdnNumber: string;
  bunkerDate?: Date;
  bunkerPort: string;
  density: number;
  densityBeforeBunkering: number;
  fuelClass: string;
  fuelGrade: SelectOption;
  fuelGradeAddedInTank: SelectOption;
  inUse: boolean;
  isBlend: boolean;
  lcv: number;
  lcvBeforeBunkering: number;
  stableUntil?: Date;
  sulphurBeforeBunkering: number;
  sulphurAfterBunkering: number;
  quantityAddedInTank: number;
  viscosity: number;
}

// The fields of a tank row that each tank table draws. Both tables write to the
// same rows in stock.tanks. An error in a field marks the tab of each table
// that draws it, as each V1 tab has its own form.
export const BUNKER_TANK_FIELDS = [
  'amountBeforeBunkering',
  'sulphurBeforeBunkering',
  'fuelGradeAddedInTank',
  'quantityAddedInTank',
  'sulphurAfterBunkering',
  'inUse',
] as const satisfies readonly (keyof StockTank)[];
export type BunkerTankField = (typeof BUNKER_TANK_FIELDS)[number];

export const STOCK_TANK_FIELDS = [
  'amount',
  'sulphurAfterBunkering',
  'lcv',
  'density',
  'viscosity',
  'fuelGrade',
  'bunkerPort',
  'bunkerDate',
  'stableUntil',
  'inUse',
] as const satisfies readonly (keyof StockTank)[];
export type StockTankField = (typeof STOCK_TANK_FIELDS)[number];

export interface StockState {
  options?: StockOptionsState;
  tankTransfers?: TankTransfersState;
  fuelWaterRob?: FuelWaterRobState;
  lubeOilRob?: LubeOilRobState;
  oilLosses?: OilLossesState;
  tanks?: StockTanks;
}
